import React, { useEffect, useState } from "react";
import {
  database,
  ref,
  onValue,
  push,
  set,
  update,
  remove,
} from "./firebase";
import { get } from "firebase/database"; // 🔹 for reading after delete
import "./BillingSystem.css";
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

const products = [
  "Premium 2 Litre Bottle",
  "Premium 1 Litre Bottle",
  "Premium 500ML Bottle",
  "Premium 250ML Bottle",
  "Green 1 Litre Bottle",
  "Green 500ML Bottle",
  "250ML Pouch",
  "Packed Ice Cube (500 gm)",
];

export default function ClosingStock({ role, phone }) {
  const [userName, setUserName] = useState(phone || "");
  const [notification, setNotification] = useState("");
  const [stockValues, setStockValues] = useState(
    Array(products.length).fill(0)
  );
  const [history, setHistory] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 3000);
  };

  const formatDate = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const formatTime = (dateObj) => {
    return dateObj.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Load user name and closing stock history
  useEffect(() => {
    const userRef = ref(database, `users/${phone}`);
    onValue(
      userRef,
      (snap) => {
        const data = snap.val();
        if (data?.name) setUserName(data.name);
      },
      { onlyOnce: true }
    );

    const historyRef = ref(database, "closingStockHistory");
    onValue(historyRef, (snap) => {
      const raw = snap.val() || {};
      const list = Object.entries(raw).map(([id, val]) => ({
        id,
        ...val,
      }));
      list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setHistory(list);
    });
  }, [phone]);

  const handleValueChange = (index, value) => {
    const arr = [...stockValues];
    arr[index] = Math.max(0, Number(value) || 0);
    setStockValues(arr);
  };

  const handleSaveClosingStock = async () => {
    const hasAny = stockValues.some((v) => v > 0);
    if (!hasAny) {
      showNotification("Enter at least one closing stock value");
      return;
    }

    const now = new Date();
    const items = products
      .map((product, i) => ({
        product,
        cartons: stockValues[i] || 0,
      }))
      .filter((it) => it.cartons > 0);

    const entry = {
      date: formatDate(now),
      time: formatTime(now),
      timestamp: now.getTime(),
      createdBy: userName,
      items,
    };

    const historyRef = ref(database, "closingStockHistory");

    if (editingId) {
      await update(ref(database, `closingStockHistory/${editingId}`), entry);
      showNotification("Closing stock entry updated");
    } else {
      await push(historyRef, entry);
      showNotification("Closing stock saved");
    }

    // Update current closingStock node (cartons per product)
    const closingMap = {};
    products.forEach((p, i) => {
      closingMap[p] = stockValues[i] || 0;
    });
    await set(ref(database, "closingStock"), closingMap);

    setEditingId(null);
    setStockValues(Array(products.length).fill(0));
  };

  const handleEditEntry = (entry) => {
    const arr = Array(products.length).fill(0);
    (entry.items || []).forEach(({ product, cartons }) => {
      const idx = products.indexOf(product);
      if (idx >= 0) {
        arr[idx] = Number(cartons) || 0;
      }
    });
    setStockValues(arr);
    setEditingId(entry.id);
    window.scrollTo(0, 0);
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Delete this closing stock entry?")) return;

    // 1️⃣ Delete from history in Realtime Database
    await remove(ref(database, `closingStockHistory/${id}`));

    // 2️⃣ Recalculate current closingStock from latest remaining history
    const historyRef = ref(database, "closingStockHistory");
    const snap = await get(historyRef);
    const raw = snap.val() || {};

    let latest = null;
    Object.entries(raw).forEach(([entryId, val]) => {
      if (!latest || (val.timestamp || 0) > (latest.timestamp || 0)) {
        latest = { id: entryId, ...val };
      }
    });

    const closingMap = {};
    products.forEach((p) => {
      closingMap[p] = 0;
    });

    if (latest && latest.items) {
      latest.items.forEach(({ product, cartons }) => {
        if (closingMap.hasOwnProperty(product)) {
          closingMap[product] = Number(cartons) || 0;
        }
      });
    }

    await set(ref(database, "closingStock"), closingMap);

    showNotification("Closing stock entry deleted");
  };

  const filteredHistory = history.filter((h) => {
    const text =
      `${h.date || ""} ${h.time || ""} ${h.createdBy || ""}`.toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="container">
      <h2>Closing Stock</h2>
      {role === "boss" && <BossNavigation />}
      <Navigation role={role} />

      {notification && (
        <div className="notification-banner">{notification}</div>
      )}

      <p>
        Logged in as <strong>{userName}</strong> ({role})
      </p>

      {["owner", "manager", "boss", "production", "stock manager"].includes(role) && (
        <>
          <div className="section">
            <h4>Set Closing Stock (Cartons)</h4>
            <div className="product-row-space">
              <span className="product-label">
                <strong>Product</strong>
              </span>
              <span className="product-input-heading">
                <strong>Cartons</strong>
              </span>
            </div>
            {products.map((p, i) => (
              <div key={i} className="product-row-space">
                <span className="product-label">{p}</span>
                <input
                  type="number"
                  className="product-input-box"
                  value={stockValues[i]}
                  onChange={(e) => handleValueChange(i, e.target.value)}
                  min="0"
                />
              </div>
            ))}
            <button onClick={handleSaveClosingStock}>
              {editingId ? "Update Closing Stock Entry" : "Save Closing Stock"}
            </button>
          </div>

          <div className="section">
            <h4>Closing Stock History</h4>
            <input
              placeholder="Search by date / time / user"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {filteredHistory.length === 0 ? (
              <p>No closing stock entries found.</p>
            ) : (
              filteredHistory.map((entry) => (
                <div key={entry.id} className="token-card">
                  <strong>
                    {entry.date} - {entry.time}
                  </strong>
                  <br />
                  <small>
                    <strong>Created By:</strong> {entry.createdBy}
                  </small>
                  <ul>
                    {(entry.items || []).map((item, idx) => (
                      <li key={idx}>
                        {item.product}: {item.cartons} cartons
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => handleEditEntry(entry)}>Edit</button>
                  <button
                    onClick={() => handleDeleteEntry(entry.id)}
                    style={{ marginLeft: 8 }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
